import { StepType } from '@/types/automationFields.ts';
import { AutomationStep, ConditionalStep } from '@/types/automationSteps.ts';

export const getPreviousSiblingStepOptions = (steps: AutomationStep[], stepId: string) =>
  getSiblingStepOptions(steps, stepId).previousSiblings;

export const getLaterSiblingStepOptions = (steps: AutomationStep[], stepId: string) =>
  getSiblingStepOptions(steps, stepId).laterSiblings;

type StepOption = {
  value: string;
  label: string;
};

type NumberedStep = {
  step: AutomationStep;
  number: number[];
};

type ChildStepCollection = {
  steps: AutomationStep[];
  parentNumber: number[];
};

const makeStepOption = ({ step, number }: NumberedStep): StepOption => ({
  value: step.step_id,
  label: `${number.join('.')} ${step.name}`,
});

const getSiblingStepOptions = (
  steps: AutomationStep[],
  stepId: string,
): { previousSiblings: StepOption[]; laterSiblings: StepOption[] } => {
  const siblingSteps = getSiblingSteps(steps, stepId);
  return {
    previousSiblings: siblingSteps.previousSiblings.map(makeStepOption),
    laterSiblings: siblingSteps.laterSiblings.map(makeStepOption),
  };
};

const getSiblingSteps = (
  steps: AutomationStep[],
  stepId: string,
  parentNumber: number[] = [],
): { previousSiblings: NumberedStep[]; laterSiblings: NumberedStep[] } => {
  for (const [index, step] of steps.entries()) {
    const number = [...parentNumber, index + 1];
    if (step.step_id === stepId) {
      return {
        previousSiblings: steps.slice(0, index).map((sibling, siblingIndex) => ({
          step: sibling,
          number: [...parentNumber, siblingIndex + 1],
        })),
        laterSiblings: steps.slice(index + 1).map((sibling, siblingIndex) => ({
          step: sibling,
          number: [...parentNumber, index + siblingIndex + 2],
        })),
      };
    }

    for (const childCollection of getChildCollections(step, number)) {
      const siblingSteps = getSiblingSteps(
        childCollection.steps,
        stepId,
        childCollection.parentNumber,
      );
      if (siblingSteps.previousSiblings.length > 0 || siblingSteps.laterSiblings.length > 0) {
        return siblingSteps;
      }
    }
  }

  return { previousSiblings: [], laterSiblings: [] };
};

const getChildCollections = (step: AutomationStep, number: number[]): ChildStepCollection[] => {
  if (
    step.step_type === StepType.Iterate ||
    step.step_type === StepType.Repeat ||
    step.step_type === StepType.Retry
  ) {
    return [{ steps: step.children, parentNumber: number }];
  }
  if (step.step_type === StepType.Conditional) {
    return (step as ConditionalStep).conditions.map((condition, conditionIndex) => ({
      steps: condition.children,
      parentNumber: [...number, conditionIndex + 1],
    }));
  }
  return [];
};
